import Phaser from 'phaser';
import { frameKey } from './assets';
import { HeroId, HEROES, MARVEL_HERO_IDS, POWERRANGERS_HERO_IDS } from './heroes';
import { levelById } from './levels';
import { getCurrentMode } from './mode';
import {
  loadSave,
  recordTeam,
  resolveEquippedStone,
  setEquippedStone,
} from './save';
import { InfinityStoneId, STONES } from './stones';

const PORTRAIT_R = 64;
const PORTRAIT_SCALE_NATIVE = 92;
const RING_STROKE = 4;
const GAP_BETWEEN = 28;

const C_CARD = 0x121a32;

function drawPadlock(g: Phaser.GameObjects.Graphics, cx: number, cy: number, size: number, color: number) {
  const bodyW = size;
  const bodyH = size * 0.8;
  const shackleR = size * 0.38;
  g.fillStyle(color, 1);
  g.fillRoundedRect(cx - bodyW / 2, cy - bodyH / 2 + shackleR * 0.3, bodyW, bodyH, 3);
  g.lineStyle(Math.max(2, Math.floor(size * 0.16)), color, 1);
  g.beginPath();
  g.arc(cx, cy - bodyH / 2 + shackleR * 0.1, shackleR, Math.PI, 0, false);
  g.strokePath();
}
const C_RING_SELECTED = 0x22c55e;
const C_RING_UNSELECTED = 0x2a3558;
const C_GO_ACTIVE = 0xffd24a;
const C_GO_DIM = 0x2a3558;
const C_STROKE = 0x0a0f1f;

export class TeamPickerScene extends Phaser.Scene {
  private levelId = '';
  private order = 0;
  private selected = new Set<HeroId>();
  private allHeroIds: HeroId[] = [];

  private portraitContainers = new Map<HeroId, Phaser.GameObjects.Container>();
  private stoneContainers = new Map<InfinityStoneId, Phaser.GameObjects.Container>();
  private goButton?: Phaser.GameObjects.Container;

  constructor() {
    super('TeamPicker');
  }

  init(data: { levelId: string }) {
    this.levelId = data.levelId;
    this.order = levelById(this.levelId)?.order ?? 0;
    const mode = getCurrentMode();
    this.allHeroIds =
      mode === 'powerrangers' ? POWERRANGERS_HERO_IDS.slice() : MARVEL_HERO_IDS.slice();
    const save = loadSave();
    const combatUnlocked = this.allHeroIds.filter(
      (id) => save.unlockedHeroes.includes(id) || mode === 'powerrangers',
    );
    const candidate = save.lastTeam.filter((id) => combatUnlocked.includes(id));
    const initial = candidate.length === 3 ? candidate : combatUnlocked.slice(0, 3);
    this.selected = new Set(initial);
    this.portraitContainers.clear();
  }

  preload() {
    for (const id of this.allHeroIds) {
      const set = HEROES[id].idleFrames();
      if (set.urls.length > 0) {
        this.load.image(frameKey(set.prefix, 0), set.urls[0]);
      }
    }
  }

  create() {
    this.cameras.main.setBackgroundColor('#0a0f1f');

    this.layout();
    this.scale.on('resize', () => this.layout());
  }

  private layout() {
    const w = this.scale.width;
    const h = this.scale.height;

    this.children.removeAll(true);
    this.portraitContainers.clear();
    this.stoneContainers.clear();

    this.drawBackButton();
    this.drawLevelBadge(w);
    this.drawPortraits(w, h);
    this.drawStonePicker(w, h);
    this.drawGoButton(w, h);
  }

  private drawStonePicker(w: number, h: number) {
    const save = loadSave();
    const earned = save.stonesEarned;
    if (earned.length === 0) return;

    const equipped = resolveEquippedStone(save);
    const r = 22;
    const gap = 18;
    const totalW = earned.length * (r * 2) + (earned.length - 1) * gap;
    const startX = Math.floor((w - totalW) / 2 + r);
    const y = Math.floor(h * 0.78);

    earned.forEach((id, i) => {
      const cx = startX + i * (r * 2 + gap);
      const container = this.add.container(cx, y);
      const spec = STONES[id];
      const isEquipped = id === equipped;

      const halo = this.add.graphics();
      if (isEquipped) {
        halo.lineStyle(4, 0xffd24a, 1);
        halo.strokeCircle(0, 0, r + 7);
      }
      container.add(halo);

      const gem = this.add.graphics();
      gem.fillStyle(spec.color, 1);
      gem.lineStyle(3, 0x0a0f1f, 1);
      gem.fillCircle(0, 0, r);
      gem.strokeCircle(0, 0, r);
      gem.fillStyle(0xffffff, 0.35);
      gem.fillCircle(-r * 0.3, -r * 0.3, r * 0.3);
      container.add(gem);

      const hit = this.add.zone(0, 0, (r + 7) * 2, (r + 7) * 2).setInteractive({ useHandCursor: true });
      hit.on('pointerup', () => this.selectStone(id));
      container.add(hit);

      this.stoneContainers.set(id, container);
    });
  }

  private selectStone(id: InfinityStoneId) {
    setEquippedStone(id);
    for (const [sid, container] of this.stoneContainers) {
      const halo = container.list[0] as Phaser.GameObjects.Graphics;
      halo.clear();
      if (sid === id) {
        halo.lineStyle(4, 0xffd24a, 1);
        halo.strokeCircle(0, 0, 22 + 7);
      }
    }
  }

  private drawBackButton() {
    const g = this.add.graphics();
    const cx = 32;
    const cy = 32;
    g.fillStyle(C_CARD, 1);
    g.lineStyle(2, C_STROKE, 1);
    g.fillCircle(cx, cy, 22);
    g.strokeCircle(cx, cy, 22);
    g.lineStyle(3, 0xffffff, 1);
    g.beginPath();
    g.moveTo(cx + 6, cy - 8);
    g.lineTo(cx - 6, cy);
    g.lineTo(cx + 6, cy + 8);
    g.strokePath();
    const hit = this.add.zone(cx, cy, 56, 56).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => this.scene.start('LevelMap'));
  }

  private drawLevelBadge(w: number) {
    const container = this.add.container(Math.floor(w / 2), 60);
    const g = this.add.graphics();
    g.fillStyle(C_CARD, 1);
    g.lineStyle(3, C_GO_ACTIVE, 1);
    g.fillRoundedRect(-36, -28, 72, 56, 12);
    g.strokeRoundedRect(-36, -28, 72, 56, 12);
    container.add(g);
    const txt = this.add.text(0, 0, String(this.order), {
      fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
      fontSize: '36px',
      color: '#ffd24a',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(txt);
  }

  private drawPortraits(w: number, h: number) {
    const count = this.allHeroIds.length;
    const rows = count > 8 ? 2 : 1;
    const cols = Math.ceil(count / rows);
    const availableW = w * 0.94;
    const slotW = Math.min(PORTRAIT_R * 2 + GAP_BETWEEN, Math.floor(availableW / cols));
    const rowSpacing = PORTRAIT_R * 2 + 48;
    const centerY = Math.floor(h * 0.52);
    const startY = centerY - ((rows - 1) * rowSpacing) / 2;

    this.allHeroIds.forEach((id, i) => {
      const row = Math.floor(i / cols);
      const col = i % cols;
      const rowStart = row * cols;
      const rowCount = Math.min(cols, count - rowStart);
      const rowTotalW = slotW * rowCount;
      const rowStartX = Math.floor((w - rowTotalW) / 2 + slotW / 2);
      const x = rowStartX + col * slotW;
      const y = Math.floor(startY + row * rowSpacing);
      this.portraitContainers.set(id, this.drawPortrait(id, x, y));
    });
  }

  private drawPortrait(id: HeroId, x: number, y: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const hero = HEROES[id];
    const set = hero.idleFrames();
    const save = loadSave();
    const unlocked = save.unlockedHeroes.includes(id);
    const isSelected = unlocked && this.selected.has(id);

    const ringG = this.add.graphics();
    this.drawPortraitRing(ringG, isSelected, unlocked);
    container.add(ringG);

    if (unlocked && set.urls.length > 0) {
      const sprite = this.add.sprite(0, 0, frameKey(set.prefix, 0));
      const scale = (PORTRAIT_R * 1.6) / PORTRAIT_SCALE_NATIVE;
      sprite.setScale(scale);
      sprite.setOrigin(0.5, 0.5);
      container.add(sprite);
    } else {
      const placeholder = this.add.graphics();
      placeholder.fillStyle(0x2a3558, 1);
      placeholder.fillCircle(0, -4, PORTRAIT_R * 0.55);
      drawPadlock(placeholder, 0, PORTRAIT_R * 0.15, PORTRAIT_R * 0.45, 0x64748b);
      container.add(placeholder);
    }

    if (unlocked) {
      const checkG = this.add.graphics();
      this.drawCheckMark(checkG, 0, PORTRAIT_R + 22, isSelected);
      container.add(checkG);

      const hit = this.add.zone(0, 0, PORTRAIT_R * 2.4, PORTRAIT_R * 2.6).setInteractive({ useHandCursor: true });
      hit.on('pointerup', () => this.toggle(id));
      container.add(hit);
    }

    return container;
  }

  private drawPortraitRing(g: Phaser.GameObjects.Graphics, selected: boolean, unlocked: boolean) {
    g.clear();
    g.fillStyle(C_CARD, 1);
    const ringColor = !unlocked ? 0x2a3558 : selected ? C_RING_SELECTED : C_RING_UNSELECTED;
    g.lineStyle(RING_STROKE, ringColor, unlocked ? 1 : 0.6);
    g.fillCircle(0, 0, PORTRAIT_R);
    g.strokeCircle(0, 0, PORTRAIT_R);
  }

  private drawCheckMark(g: Phaser.GameObjects.Graphics, cx: number, cy: number, selected: boolean) {
    g.clear();
    if (selected) {
      g.fillStyle(C_RING_SELECTED, 1);
      g.fillCircle(cx, cy, 14);
      g.lineStyle(3, 0xffffff, 1);
      g.beginPath();
      g.moveTo(cx - 6, cy);
      g.lineTo(cx - 2, cy + 5);
      g.lineTo(cx + 7, cy - 5);
      g.strokePath();
    } else {
      g.fillStyle(0x2a3558, 1);
      g.fillCircle(cx, cy, 14);
      g.lineStyle(3, 0x64748b, 1);
      g.beginPath();
      g.moveTo(cx - 5, cy - 5);
      g.lineTo(cx + 5, cy + 5);
      g.moveTo(cx + 5, cy - 5);
      g.lineTo(cx - 5, cy + 5);
      g.strokePath();
    }
  }

  private toggle(id: HeroId) {
    if (this.selected.has(id)) {
      this.selected.delete(id);
    } else {
      if (this.selected.size >= 3) return;
      this.selected.add(id);
    }
    this.redrawSelection();
  }

  private redrawSelection() {
    const save = loadSave();
    for (const [id, container] of this.portraitContainers) {
      const unlocked = save.unlockedHeroes.includes(id);
      const isSelected = unlocked && this.selected.has(id);
      const ringG = container.list[0] as Phaser.GameObjects.Graphics;
      this.drawPortraitRing(ringG, isSelected, unlocked);
      if (unlocked) {
        const checkG = container.list[2] as Phaser.GameObjects.Graphics | undefined;
        if (checkG) this.drawCheckMark(checkG, 0, PORTRAIT_R + 22, isSelected);
      }
    }
    this.redrawGoButton();
  }

  private drawGoButton(w: number, h: number) {
    const cx = Math.floor(w / 2);
    const cy = Math.floor(h * 0.9);

    const container = this.add.container(cx, cy);
    const g = this.add.graphics();
    container.add(g);

    const active = this.selected.size === 3;
    g.fillStyle(active ? C_GO_ACTIVE : C_GO_DIM, 1);
    g.lineStyle(3, C_STROKE, 1);
    g.fillRoundedRect(-80, -32, 160, 64, 20);
    g.strokeRoundedRect(-80, -32, 160, 64, 20);

    g.lineStyle(5, active ? 0x0a0f1f : 0x8ca0c4, 1);
    g.beginPath();
    g.moveTo(-18, 0);
    g.lineTo(18, 0);
    g.moveTo(6, -12);
    g.lineTo(18, 0);
    g.lineTo(6, 12);
    g.strokePath();

    const hit = this.add.zone(0, 0, 180, 80).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => {
      if (this.selected.size !== 3) return;
      const team = Array.from(this.selected);
      recordTeam(team);
      const stone = resolveEquippedStone(loadSave());
      this.scene.start('Level', { levelId: this.levelId, team, stone });
    });
    container.add(hit);

    this.goButton = container;
  }

  private redrawGoButton() {
    if (!this.goButton) return;
    const g = this.goButton.list[0] as Phaser.GameObjects.Graphics;
    const active = this.selected.size === 3;
    g.clear();
    g.fillStyle(active ? C_GO_ACTIVE : C_GO_DIM, 1);
    g.lineStyle(3, C_STROKE, 1);
    g.fillRoundedRect(-80, -32, 160, 64, 20);
    g.strokeRoundedRect(-80, -32, 160, 64, 20);
    g.lineStyle(5, active ? 0x0a0f1f : 0x8ca0c4, 1);
    g.beginPath();
    g.moveTo(-18, 0);
    g.lineTo(18, 0);
    g.moveTo(6, -12);
    g.lineTo(18, 0);
    g.lineTo(6, 12);
    g.strokePath();
  }
}
